import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false); // Start with false for public pages
  const [initializing, setInitializing] = useState(true);

  const checkAdminStatus = async (userId: string) => {
    try {
      console.log('AuthContext - Checking admin status for:', userId);
      const { data, error } = await supabase
        .from('admin_users')
        .select('user_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('AuthContext - Admin check error:', error);
        setIsAdmin(false);
        return false;
      }

      const adminStatus = !!data;
      console.log('AuthContext - Admin status result:', adminStatus, data);
      setIsAdmin(adminStatus);
      return adminStatus;
    } catch (error) {
      console.error('AuthContext - Admin check exception:', error);
      setIsAdmin(false);
      return false;
    }
  };

  useEffect(() => {
    let mounted = true;
    let subscription: any = null;

    const initializeAuth = async () => {
      try {
        console.log('AuthContext - Initializing auth...');
        
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('AuthContext - Session check:', session?.user?.email || 'No session');
        
        if (error) {
          console.error('AuthContext - Session error:', error);
          if (mounted) {
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
            setInitializing(false);
          }
          return;
        }
        
        if (session?.user && mounted) {
          console.log('AuthContext - Setting user:', session.user.email);
          setUser(session.user);
          await checkAdminStatus(session.user.id);
        } else {
          if (mounted) {
            setUser(null);
            setIsAdmin(false);
          }
        }

        // Set up auth state listener
        const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (!mounted) return;

          console.log('AuthContext - Auth state changed:', event, session?.user?.email || 'No user');
          setLoading(true);
          
          try {
            if (session?.user) {
              setUser(session.user);
              await checkAdminStatus(session.user.id);
            } else {
              setUser(null);
              setIsAdmin(false);
            }
          } catch (error) {
            console.error('AuthContext - Auth state change error:', error);
            setUser(null);
            setIsAdmin(false);
          } finally {
            if (mounted) {
              setLoading(false);
            }
          }
        });

        subscription = authSubscription;

      } catch (error) {
        console.error('AuthContext - Initialization error:', error);
        if (mounted) {
          setUser(null);
          setIsAdmin(false);
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setInitializing(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        throw error;
      }
      
      if (data.user) {
        const adminStatus = await checkAdminStatus(data.user.id);
        if (!adminStatus) {
          throw new Error('User is not an admin');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      setUser(null);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAdmin,
      loading,
      signIn,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};